import { useEffect, useState } from "react";
import { Sparkles, Plus, Edit3, Trash2, Loader2, AlertCircle, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/flows`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CATS = ["Sales", "Field Force", "Operations", "Finance", "HR", "Intelligence", "Scheduled", "Custom"];

function emptyForm() {
  return { event: "", title: "", description: "", category: "Custom", sub_category: "General", variables: "" };
}

export default function CustomTriggersPage() {
  const [rows, setRows] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | event-code-being-edited
  const [form, setForm] = useState(emptyForm());

  const reload = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/custom-triggers`, { headers: auth() }).then(r => r.json()),
      fetch(API, { headers: auth() }).then(r => r.json()),
    ]).then(([a, b]) => {
      setRows(a.data || []);
      setFlows(b.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const usedBy = (event) => flows.filter(f => (f.trigger || {}).event === event);

  const startEdit = (row) => {
    setEditing(row.event);
    setForm({
      event: row.event,
      title: row.title || "",
      description: row.description || "",
      category: row.category || "Custom",
      sub_category: row.sub_category || "General",
      variables: (row.variables || []).join(", "),
    });
  };

  const startNew = () => { setEditing("new"); setForm(emptyForm()); };
  const cancel = () => { setEditing(null); setForm(emptyForm()); };

  const save = async () => {
    const payload = {
      event: form.event.trim().toUpperCase().replace(/\s+/g, "_"),
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      sub_category: form.sub_category,
      variables: form.variables.split(",").map(s => s.trim()).filter(Boolean),
    };
    const isNew = editing === "new";
    const url = isNew ? `${API}/custom-triggers` : `${API}/custom-triggers/${editing}`;
    const r = await fetch(url, {
      method: isNew ? "POST" : "PUT",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = await r.json();
    if (j.success) {
      toast.success(isNew ? "Trigger created" : "Trigger updated");
      cancel(); reload();
    } else {
      toast.error(j.error?.message || j.detail || "Save failed");
    }
  };

  const del = async (row) => {
    const inUse = usedBy(row.event);
    if (inUse.length > 0) {
      return toast.error(`Cannot delete — used by ${inUse.length} flow(s). Re-point or delete those flows first.`);
    }
    if (!confirm(`Delete custom trigger "${row.event}"?`)) return;
    const r = await fetch(`${API}/custom-triggers/${row.event}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success("Deleted"); reload(); } else toast.error("Delete failed");
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="custom-triggers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="text-amber-500" size={28} /> Custom Triggers
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Define your own event codes so any system can fire flows. Use them via <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">POST /api/v1/flows/_emit/&lt;event&gt;</code>.
          </p>
        </div>
        {editing !== "new" && (
          <Button onClick={startNew} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="new-custom-trigger-btn">
            <Plus size={16} className="mr-1" /> New trigger
          </Button>
        )}
      </div>

      {(editing === "new" || (editing && rows.find(r => r.event === editing))) && (
        <Card className="p-5 mb-6 border-2 border-amber-400 dark:bg-slate-800" data-testid="custom-trigger-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold dark:text-white">{editing === "new" ? "New trigger" : `Edit ${editing}`}</h3>
            <button onClick={cancel}><X size={18} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Event code</label>
              <Input
                value={form.event}
                onChange={(e) => setForm({ ...form, event: e.target.value })}
                disabled={editing !== "new"}
                placeholder="DEALER_BIRTHDAY"
                data-testid="ct-event-input"
              />
              <div className="text-[10px] text-slate-400 mt-1">UPPER_SNAKE_CASE. 3–50 chars. Cannot change after creation.</div>
            </div>
            <div>
              <label className="text-xs font-medium">Friendly title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="When a dealer has a birthday" data-testid="ct-title-input" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="When and why this trigger fires" />
            </div>
            <div>
              <label className="text-xs font-medium">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Sub-category</label>
              <Input value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} placeholder="e.g. Birthdays" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Variables (comma-separated)</label>
              <Input value={form.variables} onChange={(e) => setForm({ ...form, variables: e.target.value })} placeholder="dealer_id, dealer_name, birthday_date" />
              <div className="text-[10px] text-slate-400 mt-1">Available as <code>{`{{var}}`}</code> in notification templates.</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={cancel}>Cancel</Button>
            <Button onClick={save} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="save-ct-btn"><Save size={14} className="mr-1" /> Save</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : rows.length === 0 && editing !== "new" ? (
        <Card className="p-10 text-center dark:bg-slate-800">
          <AlertCircle size={32} className="mx-auto text-slate-400 mb-2" />
          <h3 className="font-medium dark:text-white">No custom triggers yet</h3>
          <p className="text-sm text-slate-500 mt-1">Create your first — flows can then react to any business event you define.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="custom-triggers-list">
          {rows.map((r) => {
            const used = usedBy(r.event);
            return (
              <Card key={r.event} className="p-4 dark:bg-slate-800" data-testid={`ct-row-${r.event}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="font-mono text-sm font-semibold text-amber-700 dark:text-amber-400">{r.event}</code>
                      <Badge variant="outline" className="text-xs">{r.category} · {r.sub_category}</Badge>
                      {used.length > 0 && <Badge className="text-xs bg-violet-600 text-white border-0">In use by {used.length}</Badge>}
                    </div>
                    <div className="font-medium text-slate-900 dark:text-white">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.description}</div>
                    {(r.variables || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {r.variables.map(v => <code key={v} className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded">{`{{${v}}}`}</code>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`edit-${r.event}`}><Edit3 size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => del(r)} disabled={used.length > 0} data-testid={`del-${r.event}`}>
                      <Trash2 size={14} className={used.length > 0 ? "text-slate-300" : "text-red-500"} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
